import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import {
  platformChoices,
  PLATFORM_LABELS,
  PITCH_FORMATS,
  PITCH_FORMAT_LABELS,
  PITCH_COMMISSION_TYPES,
  PITCH_COMMISSION_TYPE_LABELS,
  USAGE_RIGHTS,
  USAGE_RIGHTS_LABELS,
  CURRENCIES,
  CURRENCY_LABELS,
  MAX_OPEN_PITCHES,
} from '@affily/shared/constants';
import { percentToBps } from '@affily/shared/money';
import { api } from '../../lib/api.js';
import { rule } from '../../lib/fieldRules.js';
import { useFetch } from '../../hooks/useFetch.js';
import { DashboardLayout } from '../../components/DashboardLayout.jsx';
import { PitchSummary } from '../../components/PitchSummary.jsx';
import { Button, Card, Field, Input, Textarea, Select, Alert, Spinner, EmptyState } from '../../components/ui.jsx';

const EMPTY = {
  title: '',
  brief: '',
  deliverables: [{ platform: 'instagram', format: 'reel', count: '1' }],
  startsAt: '',
  endsAt: '',
  commissionType: 'flat_per_post',
  ratePercent: '',
  flatAmount: '',
  currency: 'eur',
  wantsProduct: false,
  productNote: '',
  usageRights: 'none',
  usageMonths: '',
  exclusivityDays: '0',
  notes: '',
};

/** The form, as the API takes it. */
function toPayload(brand, form) {
  const isPercent = form.commissionType === 'percent_of_sale';
  const isFlat = ['flat_per_post', 'flat_per_conversion'].includes(form.commissionType);
  const isGift = form.commissionType === 'gift_only';
  return {
    brand,
    title: form.title,
    brief: form.brief,
    deliverables: form.deliverables.map((d) => ({ ...d, count: Number(d.count || 0) })),
    startsAt: form.startsAt ? new Date(form.startsAt).toISOString() : null,
    endsAt: form.endsAt ? new Date(form.endsAt).toISOString() : null,
    commissionType: form.commissionType,
    rateBps: isPercent && form.ratePercent ? percentToBps(Number(form.ratePercent)) : null,
    flatAmountCents: isFlat && form.flatAmount ? Math.round(Number(form.flatAmount) * 100) : null,
    currency: form.currency,
    // Gift only is all product; otherwise it rides on top, if asked for.
    wantsProduct: isGift || form.wantsProduct,
    productNote: isGift || form.wantsProduct ? form.productNote || null : null,
    usageRights: form.usageRights,
    usageMonths: form.usageRights !== 'none' && form.usageMonths ? Number(form.usageMonths) : null,
    exclusivityDays: Number(form.exclusivityDays || 0),
    notes: form.notes || null,
  };
}

/**
 * Pitch a campaign: a creator suggests one to a brand, with the brief (what
 * they'd make and why it fits) and the conditions (what they want for it).
 *
 * The brand says yes, adjusting the amount if it likes, and the pitch becomes
 * a private program the creator is invited to — or says no, with a note.
 */
export default function PitchNew() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const slug = params.get('brand');
  const { data, error: loadError, loading } = useFetch(slug ? `/brands/${slug}` : null, { skip: !slug });
  const [form, setForm] = useState(EMPTY);
  const [preview, setPreview] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  const brand = data?.brand;
  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const setDeliverable = (i, key) => (e) =>
    setForm((f) => ({
      ...f,
      deliverables: f.deliverables.map((d, j) => (j === i ? { ...d, [key]: e.target.value } : d)),
    }));
  const addDeliverable = () =>
    setForm((f) => ({ ...f, deliverables: [...f.deliverables, { platform: 'tiktok', format: 'video', count: '1' }] }));
  const removeDeliverable = (i) =>
    setForm((f) => ({ ...f, deliverables: f.deliverables.filter((_, j) => j !== i) }));

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError('');
    setFieldErrors({});
    try {
      await api.post('/pitches', toPayload(brand.slug, form));
      navigate('/dashboard/creator/pitches?sent=1');
    } catch (err) {
      setError(err.message);
      setFieldErrors(err.details ?? {});
      setPreview(false);
    } finally {
      setBusy(false);
    }
  }

  if (!slug) {
    return (
      <DashboardLayout side="creator" title="Pitch a campaign">
        <EmptyState title="Choose a brand first" action={<Button to="/programs">Browse brands’ programs</Button>}>
          Open a brand’s page and press “Pitch a campaign”.
        </EmptyState>
      </DashboardLayout>
    );
  }

  if (loading && !data) {
    return (
      <DashboardLayout side="creator" title="Pitch a campaign">
        <Spinner />
      </DashboardLayout>
    );
  }

  if (loadError || !brand) {
    return (
      <DashboardLayout side="creator" title="Pitch a campaign">
        <Alert>{loadError?.message ?? 'No such brand'}</Alert>
      </DashboardLayout>
    );
  }

  if (!brand.acceptsPitches) {
    return (
      <DashboardLayout side="creator" title={`Pitch ${brand.companyName}`}>
        <EmptyState
          title={`${brand.companyName} isn’t taking pitches right now`}
          action={<Button to={`/brands/${brand.slug}`} variant="outline">Back to {brand.companyName}</Button>}
        >
          You can still apply to any of its open programs.
        </EmptyState>
      </DashboardLayout>
    );
  }

  const payload = toPayload(brand.slug, form);
  const isPercent = form.commissionType === 'percent_of_sale';
  const isFlat = ['flat_per_post', 'flat_per_conversion'].includes(form.commissionType);
  const isGift = form.commissionType === 'gift_only';

  return (
    <DashboardLayout
      side="creator"
      title={`Pitch ${brand.companyName}`}
      description="Suggest a campaign: what you’d make, and what you’d like for it. The brand can say yes, adjust the amount, or say no with a note."
    >
      <form onSubmit={submit} className="max-w-3xl space-y-6">
        <Alert>{error}</Alert>

        {preview ? (
          <Card className="p-5">
            <p className="mb-3 text-2xs font-semibold uppercase tracking-wider text-ink-faint">
              What {brand.companyName} will see
            </p>
            <h2 className="mb-3 text-lg font-semibold">{form.title || 'Untitled pitch'}</h2>
            <PitchSummary pitch={payload} />
          </Card>
        ) : (
          <>
            <Card className="p-5">
              <h2 className="mb-4 font-semibold">The brief</h2>
              <div className="space-y-4">
                <Field label="Title" required error={fieldErrors.title}>
                  <Input value={form.title} onChange={set('title')} maxLength={140} placeholder="Summer rooftop series" required />
                </Field>
                <Field
                  label="Your idea"
                  required
                  error={fieldErrors.brief}
                  hint="What you’d make, who your audience is, and why it fits this brand."
                  constraint={`${form.brief.length}/4000`}
                >
                  <Textarea value={form.brief} onChange={set('brief')} maxLength={4000} rows={6} required />
                </Field>

                <fieldset>
                  <legend className="mb-1.5 text-sm font-medium text-ink">
                    What you’d deliver<span className="ml-0.5 text-negative">*</span>
                  </legend>
                  <div className="space-y-2">
                    {form.deliverables.map((d, i) => (
                      <div key={i} className="flex flex-wrap items-center gap-2">
                        <Input
                          type="number"
                          min="1"
                          max="50"
                          value={d.count}
                          onChange={setDeliverable(i, 'count')}
                          aria-label="How many"
                          className="w-20"
                        />
                        <Select value={d.format} onChange={setDeliverable(i, 'format')} aria-label="Format" className="w-40">
                          {PITCH_FORMATS.map((f) => (
                            <option key={f} value={f}>
                              {PITCH_FORMAT_LABELS[f]}
                            </option>
                          ))}
                        </Select>
                        <span className="text-sm text-ink-muted">on</span>
                        <Select value={d.platform} onChange={setDeliverable(i, 'platform')} aria-label="Platform" className="w-40">
                          {platformChoices([d.platform]).map((p) => (
                            <option key={p} value={p}>
                              {PLATFORM_LABELS[p]}
                            </option>
                          ))}
                        </Select>
                        {form.deliverables.length > 1 && (
                          <Button type="button" variant="ghost" size="sm" onClick={() => removeDeliverable(i)}>
                            Remove
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                  {form.deliverables.length < 10 && (
                    <Button type="button" variant="outline" size="sm" className="mt-2" onClick={addDeliverable}>
                      + Add another
                    </Button>
                  )}
                  {fieldErrors.deliverables && (
                    <p role="alert" className="mt-1 text-xs font-medium text-negative">
                      {fieldErrors.deliverables}
                    </p>
                  )}
                </fieldset>

                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Starts" hint="Optional" error={fieldErrors.startsAt}>
                    <Input type="date" value={form.startsAt} onChange={set('startsAt')} />
                  </Field>
                  <Field label="Ends" hint="Optional" error={fieldErrors.endsAt}>
                    <Input type="date" value={form.endsAt} onChange={set('endsAt')} />
                  </Field>
                </div>
              </div>
            </Card>

            <Card className="p-5">
              <h2 className="mb-4 font-semibold">Your conditions</h2>
              <div className="space-y-4">
                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="How you’d like to be paid" required>
                    <Select value={form.commissionType} onChange={set('commissionType')}>
                      {PITCH_COMMISSION_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {PITCH_COMMISSION_TYPE_LABELS[t]}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  {!isGift && (
                    <Field label="Currency" required>
                      <Select value={form.currency} onChange={set('currency')}>
                        {CURRENCIES.map((c) => (
                          <option key={c} value={c}>
                            {CURRENCY_LABELS[c]}
                          </option>
                        ))}
                      </Select>
                    </Field>
                  )}
                </div>

                {isPercent && (
                  <Field label="% of each sale" required error={fieldErrors.rateBps} constraint={rule.percent()}>
                    <Input type="number" min="0.01" max="100" step="0.01" value={form.ratePercent} onChange={set('ratePercent')} required />
                  </Field>
                )}
                {isFlat && (
                  <Field
                    label={`Amount per ${form.commissionType === 'flat_per_post' ? 'post or video' : 'sale or lead'} (${form.currency.toUpperCase()})`}
                    required
                    error={fieldErrors.flatAmountCents}
                    constraint={rule.money('250.00')}
                  >
                    <Input type="number" min="1" step="0.01" value={form.flatAmount} onChange={set('flatAmount')} required />
                  </Field>
                )}

                {!isGift && (
                  <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={form.wantsProduct} onChange={set('wantsProduct')} />
                    I’d also like the product, to show it
                  </label>
                )}
                {(isGift || form.wantsProduct) && (
                  <Field
                    label={isGift ? 'Which product or gift' : 'Which product'}
                    required={isGift}
                    error={fieldErrors.productNote}
                  >
                    <Input value={form.productNote} onChange={set('productNote')} maxLength={500} placeholder="Dinner for two" />
                  </Field>
                )}

                <div className="grid gap-4 sm:grid-cols-2">
                  <Field label="Can the brand use your content?" error={fieldErrors.usageRights}>
                    <Select value={form.usageRights} onChange={set('usageRights')}>
                      {USAGE_RIGHTS.map((u) => (
                        <option key={u} value={u}>
                          {USAGE_RIGHTS_LABELS[u]}
                        </option>
                      ))}
                    </Select>
                  </Field>
                  {form.usageRights !== 'none' && (
                    <Field label="For how many months" required error={fieldErrors.usageMonths}>
                      <Input type="number" min="1" max="60" value={form.usageMonths} onChange={set('usageMonths')} required />
                    </Field>
                  )}
                </div>

                <Field
                  label="Exclusivity (days)"
                  hint="How long you’d not work with a competing brand. 0 for none."
                  error={fieldErrors.exclusivityDays}
                >
                  <Input type="number" min="0" max="365" value={form.exclusivityDays} onChange={set('exclusivityDays')} className="w-32" />
                </Field>

                <Field label="Anything else" hint="Optional: approvals, revisions, timing…" error={fieldErrors.notes}>
                  <Textarea value={form.notes} onChange={set('notes')} maxLength={2000} rows={3} />
                </Field>
              </div>
            </Card>
          </>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <Button type="submit" disabled={busy}>
            {busy ? 'Sending…' : `Send to ${brand.companyName}`}
          </Button>
          <Button type="button" variant="outline" onClick={() => setPreview((p) => !p)}>
            {preview ? 'Back to editing' : 'Preview'}
          </Button>
          <Link to={`/brands/${brand.slug}`} className="text-sm text-ink-muted hover:text-ink">
            Cancel
          </Link>
        </div>
        <p className="text-xs text-ink-faint">
          One pitch per brand at a time, and up to {MAX_OPEN_PITCHES} waiting for an answer. If{' '}
          {brand.companyName} says yes, you’ll get an invitation with the final terms to accept.
        </p>
      </form>
    </DashboardLayout>
  );
}
